// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';

/**
 * Layered projection-assumption overrides (Global → Brokerage → Advisor →
 * Relationship), resolved per key. This client covers the advisor-facing scopes:
 * the relationship override (+ effective/provenance), the advisor's own override,
 * and — for advisors with the ADMIN role — their brokerage override.
 * See docs/architecture/layered-projection-assumptions.md.
 */

export interface ProjectionAssumptions {
  priceInflationRate: number | null;
  earningsGrowthRate: number | null;
  statePensionAnnual: number | null;
  pensionDrawdownRate: number | null;
  expenditureAtRetirementFactor: number | null;
  defaultSavingsGrowthRate: number | null;
  defaultInvestmentGrowthRate: number | null;
  defaultPropertyGrowthRate: number | null;
  defaultCollectableGrowthRate: number | null;
  defaultSurplusSweepRate: number | null;
  childBenefitMonthly: number | null;
  horizonYears: number | null;
}

export type AssumptionSource =
  | 'RELATIONSHIP' | 'ADVISOR' | 'BROKERAGE' | 'GLOBAL' | 'CONFIG' | 'UNSET';

/** Resolved values + per-key provenance for a relationship. */
export interface EffectiveAssumptions extends ProjectionAssumptions {
  sources: Record<string, AssumptionSource>;
}

/** The editable keys, in display order, with friendly labels + unit. */
export const ASSUMPTION_FIELDS: Array<{
  key: keyof ProjectionAssumptions;
  label: string;
  suffix: '%' | '€' | '×' | 'yrs';
}> = [
  { key: 'priceInflationRate', label: 'Price inflation', suffix: '%' },
  { key: 'earningsGrowthRate', label: 'Earnings growth', suffix: '%' },
  { key: 'statePensionAnnual', label: 'State pension (annual)', suffix: '€' },
  { key: 'pensionDrawdownRate', label: 'Pension drawdown', suffix: '%' },
  { key: 'expenditureAtRetirementFactor', label: 'Expenditure at retirement', suffix: '×' },
  { key: 'defaultSavingsGrowthRate', label: 'Default savings growth', suffix: '%' },
  { key: 'defaultInvestmentGrowthRate', label: 'Default investment growth', suffix: '%' },
  { key: 'defaultPropertyGrowthRate', label: 'Default property growth', suffix: '%' },
  { key: 'defaultCollectableGrowthRate', label: 'Default collectable growth', suffix: '%' },
  { key: 'defaultSurplusSweepRate', label: 'Surplus sweep rate', suffix: '%' },
  { key: 'childBenefitMonthly', label: 'Child Benefit (per child, monthly)', suffix: '€' },
  { key: 'horizonYears', label: 'Projection horizon', suffix: 'yrs' },
];

const empty: ProjectionAssumptions = {
  priceInflationRate: null, earningsGrowthRate: null, statePensionAnnual: null,
  pensionDrawdownRate: null, expenditureAtRetirementFactor: null,
  defaultSavingsGrowthRate: null, defaultInvestmentGrowthRate: null,
  defaultPropertyGrowthRate: null, defaultCollectableGrowthRate: null,
  defaultSurplusSweepRate: null, childBenefitMonthly: null, horizonYears: null,
};

/** A relationship "clear" = save an all-null override so every key re-inherits. */
export const EMPTY_ASSUMPTIONS: ProjectionAssumptions = empty;

export const projectionAssumptionsApi = {
  // Relationship (override lives at the cashflow path; effective is resolved).
  getEffective: async (profileId: string): Promise<EffectiveAssumptions> => {
    const { data } = await api.get<EffectiveAssumptions>(
      `/profiles/${profileId}/projection-assumptions/effective`,
    );
    return data;
  },
  /** What this relationship inherits when unset (Advisor + Brokerage + Global + config). */
  getRelationshipInherited: async (profileId: string): Promise<EffectiveAssumptions> => {
    const { data } = await api.get<EffectiveAssumptions>(
      `/profiles/${profileId}/projection-assumptions/inherited`,
    );
    return data;
  },
  getRelationship: async (profileId: string): Promise<ProjectionAssumptions> => {
    const { data } = await api.get<ProjectionAssumptions>(
      `/profiles/${profileId}/financial-position/cashflow/assumptions`,
    );
    return data;
  },
  saveRelationship: async (profileId: string, body: ProjectionAssumptions): Promise<ProjectionAssumptions> => {
    const { data } = await api.put<ProjectionAssumptions>(
      `/profiles/${profileId}/financial-position/cashflow/assumptions`, body,
    );
    return data;
  },

  // Advisor (self).
  getAdvisor: async (): Promise<ProjectionAssumptions> => {
    const { data } = await api.get<ProjectionAssumptions>('/advisors/me/projection-assumptions');
    return data;
  },
  getAdvisorInherited: async (): Promise<EffectiveAssumptions> => {
    const { data } = await api.get<EffectiveAssumptions>('/advisors/me/projection-assumptions/inherited');
    return data;
  },
  saveAdvisor: async (body: ProjectionAssumptions): Promise<ProjectionAssumptions> => {
    const { data } = await api.put<ProjectionAssumptions>('/advisors/me/projection-assumptions', body);
    return data;
  },
  clearAdvisor: async (): Promise<void> => {
    await api.delete('/advisors/me/projection-assumptions');
  },

  // Brokerage (own — advisor with ADMIN role).
  getBrokerage: async (): Promise<ProjectionAssumptions> => {
    const { data } = await api.get<ProjectionAssumptions>('/brokerages/current/projection-assumptions');
    return data;
  },
  getBrokerageInherited: async (): Promise<EffectiveAssumptions> => {
    const { data } = await api.get<EffectiveAssumptions>('/brokerages/current/projection-assumptions/inherited');
    return data;
  },
  saveBrokerage: async (body: ProjectionAssumptions): Promise<ProjectionAssumptions> => {
    const { data } = await api.put<ProjectionAssumptions>('/brokerages/current/projection-assumptions', body);
    return data;
  },
  clearBrokerage: async (): Promise<void> => {
    await api.delete('/brokerages/current/projection-assumptions');
  },
};
